import {
 BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import getBrandName from "app/shared/utils/BrandHelper";
import { GetBalanceRequest, RewardsAccountResponse } from "app/features/Categories/types/RewardsAccountType";
import { LoginRequest, LoginResponseType, LogoutResponseType } from "app/features/Login/types/AuthResponseType";
import { AutoReloadRequest, UserDetailRequest, UserDetailResponse } from "app/features/Login/types/UserDetailResponseType";

// eslint-disable-next-line import/no-cycle
import {
    OrderSubmitType,
    OrderSubmitTypeWithCAPI,
    ScheduleGuestOrderRequest,
    ScheduleLoyaltyOrderRequest,
} from "app/features/Order/types/OrderSubmitType";
import { FinalOrderResponse } from "app/features/Order/types/FinalOrderResponseType";
import { encryptURI } from "app/shared/utils/ValueEncryption";
import { GuestRequest, GuestResponseType } from 'app/features/Guest/types/Guest';
import { RedeemRewardRequest, RedeemRewardsResponse } from "app/features/Rewards/types/UserRewardsDetails";
import {
    AddFundsRequest,
    FiservAddFundsRequest,
    DeleteFiservCardRequest,
    UpdateFiservCardRequest,
    VaultFiserCardRequest,
} from "app/features/LoyalityAddFunds/types/AddFundsRequest";
import { UpdateBillingRequest } from "app/features/LoyalityAddFunds/types/BillingUpdate";
import { SaleSubmitType } from 'app/features/Order/types/SaleSubmitType';
import { encrypt } from "app/shared/utils/EncryptionUtility";
import { ForgotPasswordRequest, ForgotPasswordResponseType } from 'app/features/MyAccount/ForgotPassword/types/ForgotPasswordType';
import { ResetPasswordRequest, ResetPasswordResponseType } from 'app/features/MyAccount/ResetPassword/types/ResetPasswordType';
import { IPersonaInfoUpdateRequest, ISignUpForm, SignUpResponseType } from 'app/features/MyAccount/types/SignUpForm';
import { StoreAvailabilityRequest, StoreAvailabilityResponse } from 'app/features/Location/store/StoreResponseType';
import { ChangePasswordRequest, ChangePasswordResponseType } from 'app/features/MyAccount/ChangePassword/types/ChangePasswordType';
import { FiservHostedPageRequest } from "app/features/Payment/types/Fiserv";
import { GiftCardCheckBalanceRequest } from 'app/features/GiftCards/GiftCardResponse';
import { UnsubscribeRequest } from 'app/features/Unsubscribe/types/UnsubscribeType';
import { VisitCodeRequest } from "app/features/RewardsHome/RewardsVisitCodeSection/VisitCodeTypes";
import { GiftCardTransferRequest } from 'app/features/MyAccount/GiftCardTransfer/types/GiftCardTypes';
import {
 setLogoutRequested,
} from 'app/store/slices/authSlice';
import { UNAUTHORIZED_CODE } from 'app/shared/constants/constants';
import ApiConstants from './apiConstants';
import getHeader from "./getHeader";
import { EncryptedResponse } from "./commonTypes";

const baseURL = process.env.REACT_APP_ALLEGIANCE_API_URL;

const { appSource } = getBrandName();

//Ignore processing force logout for below endpoints
const WHITELIST_ENDPOINTS = ["api/login"];

/**
 * Base RTK Query fetch configuration
 *
 * - `baseUrl`: Root API URL that all requests will be prefixed with
 * - `prepareHeaders`: Custom function to enrich every request with required headers
 *    (e.g., auth token, guest token, content type). Uses Redux state + existing headers.
 * - `credentials`:
 *    - On `localhost` → set to `'include'` so cookies (auth/session) are sent along with requests
 *      as react and express run on different ports.
 *    - On dev, qa, prod → undefined, as react and express sahre same domain and port.
 *
 * This `rawBaseQuery` is wrapped by `customBaseQuery` so that we can intercept
 * JWT validation failures and trigger logout flows in one place.
 */
export const rawBaseQuery = fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => getHeader(getState, headers),
    credentials: window.location.hostname === 'localhost' ? 'include' : undefined,
});

/**
 * Custom base query wrapper for RTK Query
 *
 * - Wraps the default `rawBaseQuery` (which handles fetch requests)
 * - Intercepts error responses to check for JWT validation failures
 * - When a token is invalid/expired (identified by `JWT_VALIDATION_FAILED_CODE`):
 *    - Dispatches `setLogoutRequested(true)` which triggers the logout flow in `LogoutListener`
 *    - Returns `{ data: null }` to prevent consumers from receiving an error object
 *
 * This ensures that:
 * - Any API call that fails JWT validation will consistently trigger a logout
 * - The logout flow is centralized in Redux/UI instead of being handled in multiple places
 */
export const customBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
    // Execute the actual API request
    const result = await rawBaseQuery(args, api, extraOptions);

    // Normalize URL (args can be string or object)
    const url = typeof args === "string" ? args : args.url;

    // If backend indicates JWT validation failure → trigger logout flow
    if (!WHITELIST_ENDPOINTS.some((endpoint) => url.includes(endpoint))
        && result.error
        && result.error.status === UNAUTHORIZED_CODE) {
        api.dispatch(setLogoutRequested(true)); // handled by LogoutListener
        return { data: null }; // prevent further propagation of error
    }
    // Otherwise just return the result as-is
    return result;
};

export const mutateGateAPI = createApi({
    reducerPath: 'authenticate',
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
        login: builder.mutation<LoginResponseType, LoginRequest>({
            query: (credentials) => ({
                url: `api/login`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(`{"sourceApp":"${appSource}", "recaptchaToken": "${credentials.recaptchaToken || 'undefined'}","authenticate":{"credentials":{"password": "${credentials.password}","userName": "${credentials.username}"}}}`) }),
            }),
        }),
        logout: builder.mutation<LogoutResponseType, void>({
            query: () => ({
                url: `api/logout`,
                method: 'POST',
            }),
        }),
        guest: builder.mutation<GuestResponseType, GuestRequest>({
            query: (guestBody) => ({
                url: `api/guest`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(`{"sourceApp": "${appSource}","authenticate":{"credentials":{"isAnonymous": true,"deviceId": "${guestBody.deviceId}"}}}`) }),
            }),
        }),
        updatePersonalInfo: builder.mutation<SignUpResponseType, IPersonaInfoUpdateRequest>({
            query: (reqBody) => ({
                url: ApiConstants.UPDATE_USER_DETAILS,
                method: 'POST',
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                body: JSON.stringify({ value: encrypt(JSON.stringify(reqBody)) }),
            }),
        }),
        createAccount: builder.mutation<SignUpResponseType, ISignUpForm>({
            query: (reqBody) => ({
                url: ApiConstants.CREATE_ACCOUNT,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(reqBody)) }),
            }),
        }),
        // eslint-disable-next-line max-len
        getUserRewards: builder.mutation<RewardsAccountResponse, GetBalanceRequest>({
            query: (balanceRequest) => ({
                url: `api/getBalance`,
                method: 'POST',
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                body: JSON.stringify({ value: encrypt(`{"uid":"${balanceRequest.user}","sourceApp":"${appSource}","getBalance":{"checkRegistration":false},"cardNumber":"${balanceRequest.cardNumber}"}`) }),
            }),
        }),
        redeemReward: builder.mutation<RedeemRewardsResponse, RedeemRewardRequest>({
            query: (redeemRewardRequest) => ({
                url: `api/redeemReward/${encryptURI(redeemRewardRequest?.user)}`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(`{"code":"${redeemRewardRequest?.code}","points":"${redeemRewardRequest?.points}","rewardId":${redeemRewardRequest?.rewardId}}`) }),
            }),
        }),
        getUserDetails: builder.mutation<UserDetailResponse, UserDetailRequest>({
            query: (userDetailRequest) => {
                const { uid, checkSmsOptInVerification = false } = userDetailRequest;
                const payload = {
                    sourceApp: appSource,
                    uid,
                    get: null,
                    checkSmsOptInVerification,
                };

                return {
                    url: `api/userDetail`,
                    method: "POST",
                    body: JSON.stringify({ value: encrypt(JSON.stringify(payload)) }),
                };
            },
        }),
        submitFinalOrder: builder.mutation<FinalOrderResponse, OrderSubmitType>({
            query: (orderBody) => ({
                url: `api/postOrder`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(orderBody)) }),
            }),
        }),
        updateFinalOrder: builder.mutation<FinalOrderResponse,
            { orderBody: OrderSubmitType, orderNumber: string }>({
                query: (queryBody) => ({
                    url: `api/updateOrder/${encryptURI(queryBody.orderNumber)}`,
                    method: 'PUT',
                    body: JSON.stringify({ value: encrypt(JSON.stringify(queryBody.orderBody)) }),
                }),
            }),
        addFundsWithCard: builder.mutation<EncryptedResponse, AddFundsRequest>({
            query: (addFundsReq) => ({
                url: `api/add/funds`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(addFundsReq)) }),
            }),
        }),
        accountBillingUpdate: builder.mutation<EncryptedResponse, UpdateBillingRequest>({
            query: (updateBillingRequest) => ({
                url: `api/account/update`,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(updateBillingRequest)) }),
            }),
        }),
        submitFullFillOrder: builder.mutation<FinalOrderResponse,
            { orderBody: OrderSubmitTypeWithCAPI, orderNumber: string }>({
                query: (queryBody) => ({
                    url: `api/fullFillOrder/${encryptURI(queryBody.orderNumber)}`,
                    method: 'POST',
                    body: JSON.stringify({ value: encrypt(JSON.stringify(queryBody.orderBody)) }),
                }),
            }),
        submitScheduledLoyaltyOrder: builder.mutation<
            FinalOrderResponse,
            ScheduleLoyaltyOrderRequest
        >({
            query: (queryBody) => ({
                url: ApiConstants.ORDER_SCHEDULE_LOYALTY,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(queryBody)) }),
            }),
        }),
        submitScheduledGuestOrder: builder.mutation<FinalOrderResponse, ScheduleGuestOrderRequest>({
            query: (queryBody) => ({
                url: ApiConstants.ORDER_SCHEDULE_GUEST,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(queryBody)) }),
            }),
        }),
        submitSaleOrder: builder.mutation<FinalOrderResponse, SaleSubmitType>({
            query: (saleSubmitType) => ({
                url: ApiConstants.CREATE_ORDER_SALE,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(saleSubmitType)) }),
            }),
        }),
        forgotPassword: builder.mutation<ForgotPasswordResponseType, ForgotPasswordRequest>({
            query: (forgotPassword) => ({
                url: ApiConstants.FORGOT_PASSWORD,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(forgotPassword)) }),
            }),
        }),
        resetPassword: builder.mutation<ResetPasswordResponseType, ResetPasswordRequest>({
            query: (resetPassword) => ({
                url: ApiConstants.RESET_PASSWORD,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(resetPassword)) }),
            }),
        }),
        updatePassword: builder.mutation<ChangePasswordResponseType, ChangePasswordRequest>({
            query: (updatePassword) => ({
                url: ApiConstants.UPDATE_PASSWORD,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(updatePassword)) }),
            }),
        }),
        applyCoupon: builder.mutation<FinalOrderResponse,
            { orderBody: OrderSubmitType, orderNumber: string }>({
                query: (queryBody) => ({
                    url: `api/orderUpdate/${encryptURI(queryBody.orderNumber)}`,
                    method: 'PUT',
                    body: JSON.stringify({ value: encrypt(JSON.stringify(queryBody.orderBody)) }),
                }),
            }),
        getFiservHostedPage: builder.mutation<EncryptedResponse, FiservHostedPageRequest>({
            query: (getPagesRequest: FiservHostedPageRequest) => ({
                url: ApiConstants.GET_HOSTED_PAGES,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(getPagesRequest)) }),
            }),
        }),
        addFundsFiservPayment: builder.mutation<EncryptedResponse, FiservAddFundsRequest>({
            query: (addFundsRequest: FiservAddFundsRequest) => ({
                url: ApiConstants.CUSTOMER_ADD_FUNDS,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(addFundsRequest)) }),
            }),
        }),
        deleteCustomerCard: builder.mutation<EncryptedResponse, DeleteFiservCardRequest>({
            query: (deleteRequest: DeleteFiservCardRequest) => ({
                url: `${ApiConstants.CUSTOMER_CARD_SINGLE
                // eslint-disable-next-line
                    .replace(':fdCustomerId', encryptURI(deleteRequest.fdCustomerId))
                // eslint-disable-next-line
                    .replace(':fdAccountId', encryptURI(deleteRequest.fdAccountId))
                  // eslint-disable-next-line
                   }?isAutoReloadCard=${!!deleteRequest.isAutoReloadCard}`,
                method: 'DELETE',
            }),
        }),
        updateCustomerCard: builder.mutation<EncryptedResponse, UpdateFiservCardRequest>({
            query: (updateRequest: UpdateFiservCardRequest) => ({
                    url: ApiConstants.CUSTOMER_CARD_SINGLE
                        // eslint-disable-next-line
                        .replace(':fdCustomerId', encryptURI(updateRequest.fdCustomerId))
                        // eslint-disable-next-line
                        .replace(':fdAccountId', encryptURI(updateRequest.fdAccountId)),
                    method: 'PUT',
                    body: JSON.stringify({ value: encrypt(JSON.stringify(updateRequest)) }),
            }),
        }),
        vaultCustomerCard: builder.mutation<EncryptedResponse, VaultFiserCardRequest>({
            query: (vaultRequest: VaultFiserCardRequest) => ({
                url: ApiConstants.CUSTOMER_VAULT_CARD,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(vaultRequest)) }),
            }),
        }),
        autoReloadFunds: builder.mutation<EncryptedResponse, AutoReloadRequest>({
            query: (updateRequest: AutoReloadRequest) => ({
                url: ApiConstants.CUSTOMER_AUTO_RELOAD_FUNDS,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(updateRequest)) }),
            }),
        }),
        checkBalance: builder.mutation<EncryptedResponse, GiftCardCheckBalanceRequest>({
            query: (reqBody: GiftCardCheckBalanceRequest) => ({
                url: ApiConstants.GIFT_CARD_CHECK_BALANCE,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(reqBody)) }),
            }),
        }),
        giftCardTransfer: builder.mutation<EncryptedResponse, GiftCardTransferRequest>({
            query: (reqBody: GiftCardTransferRequest) => ({
                url: ApiConstants.GIFT_CARD_TRANSFER,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(reqBody)) }),
            }),
        }),
        applyVisitCode: builder.mutation<EncryptedResponse, VisitCodeRequest>({
            query: (reqBody: VisitCodeRequest) => ({
                url: ApiConstants.RECEIPT_VISIT_CODE,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(reqBody)) }),
            }),
        }),
        storeAvailability: builder.mutation<StoreAvailabilityResponse, StoreAvailabilityRequest>({
            query: (storeAvailability: StoreAvailabilityRequest) => ({
                url: ApiConstants.STORE_AVALABILITY,
                method: "POST",
                body: storeAvailability,
            }),
        }),
        unsubscribe: builder.mutation<EncryptedResponse, UnsubscribeRequest>({
            query: (unsubscribe) => ({
                url: ApiConstants.UNSUBSCIBE,
                method: 'POST',
                body: JSON.stringify({ value: encrypt(JSON.stringify(unsubscribe)) }),
            }),
        }),
    }),
});

export const {
    useLoginMutation,
    useLogoutMutation,
    useGuestMutation,
    useUpdatePersonalInfoMutation,
    useCreateAccountMutation,
    useGetUserDetailsMutation,
    useGetUserRewardsMutation,
    useRedeemRewardMutation,
    useSubmitFinalOrderMutation,
    useUpdateFinalOrderMutation,
    useAddFundsWithCardMutation,
    useAccountBillingUpdateMutation,
    useStoreAvailabilityMutation,
    useSubmitFullFillOrderMutation,
    useSubmitScheduledLoyaltyOrderMutation,
    useSubmitScheduledGuestOrderMutation,
    useSubmitSaleOrderMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useUpdatePasswordMutation,
    useApplyCouponMutation,
    useGetFiservHostedPageMutation,
    useAddFundsFiservPaymentMutation,
    useDeleteCustomerCardMutation,
    useUpdateCustomerCardMutation,
    useCheckBalanceMutation,
    useGiftCardTransferMutation,
    useUnsubscribeMutation,
    useVaultCustomerCardMutation,
    useAutoReloadFundsMutation,
    useApplyVisitCodeMutation,
} = mutateGateAPI;
